/**
 * The marker detail card shown inside a MapLibre popup.
 *
 * Built as real DOM (not an HTML string) so listeners attach safely and any
 * text — including our own notes — is inserted as text nodes, never parsed as
 * markup. Latin place names are wrapped in <bdi> so they render left-to-right
 * inside the RTL Hebrew card.
 */

import type { Category } from '../lib/types';
import { categoryColor, categoryLabelHe } from '../lib/colors';
import { googleMapsUrl, wazeUrl, appleMapsUrl, type NavTarget } from '../lib/navigate';
import { isIOS } from './platform';

export interface CardData extends NavTarget {
  name: string;
  category: Category;
  /** Time ("HH:MM") for stops, or night range for hotels. */
  meta?: string;
  note?: string;
  url?: string;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function navLink(label: string, href: string, external: boolean): HTMLAnchorElement {
  const a = el('a', 'card__btn');
  a.textContent = label;
  a.href = href;
  a.rel = 'noopener';
  if (external) a.target = '_blank';
  return a;
}

/** Build the detail card element for a place. */
export function buildCard(data: CardData): HTMLElement {
  const card = el('div', 'card');
  card.setAttribute('dir', 'rtl');
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-label', data.name);
  card.tabIndex = -1;

  const head = el('div', 'card__head');
  const swatch = el('span', 'card__swatch');
  swatch.style.background = categoryColor(data.category);
  swatch.setAttribute('aria-hidden', 'true');
  head.appendChild(swatch);

  const titleWrap = el('div', 'card__titlewrap');
  const title = el('h3', 'card__title');
  const name = el('bdi'); // isolate Latin script within RTL
  name.textContent = data.name;
  title.appendChild(name);
  titleWrap.appendChild(title);

  const sub = el('div', 'card__sub');
  if (data.meta) {
    const time = el('span', 'card__time', data.meta);
    sub.appendChild(time);
  }
  sub.appendChild(el('span', 'card__cat', categoryLabelHe(data.category)));
  titleWrap.appendChild(sub);
  head.appendChild(titleWrap);
  card.appendChild(head);

  if (data.note) {
    card.appendChild(el('p', 'card__note', data.note));
  }

  const actions = el('div', 'card__actions');
  const primary = navLink('נווט · Google', googleMapsUrl(data), true);
  primary.classList.add('card__btn--primary');
  actions.appendChild(primary);
  actions.appendChild(navLink('Waze', wazeUrl(data), true));
  if (isIOS()) {
    actions.appendChild(navLink('Apple Maps', appleMapsUrl(data), false));
  }
  if (data.url) {
    const info = navLink('מידע', data.url, true);
    info.classList.add('card__btn--ghost');
    actions.appendChild(info);
  }
  card.appendChild(actions);

  return card;
}
